import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Shield, ShieldCheck, ShieldOff, Copy, Check } from "lucide-react";

interface SetupData {
  secret: string;
  qrCodeDataUrl: string;
  backupCodes: string[];
}

type SetupStep = "idle" | "scan" | "verify";

export function TwoFactorSetup() {
  const { t } = useTranslation();
  const [step, setStep] = useState<SetupStep>("idle");
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [copiedCodes, setCopiedCodes] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: status } = useQuery({
    queryKey: ["/api/auth/2fa/status"],
    queryFn: () => apiRequest("GET", "/api/auth/2fa/status"),
    retry: false,
  });

  const isEnabled = (status as { enabled?: boolean } | undefined)?.enabled === true;

  const setupMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/auth/2fa/setup"),
    onSuccess: (data: SetupData) => {
      setSetupData(data);
      setStep("scan");
    },
    onError: (error: Error) => {
      toast({
        title: t("twoFactor.toast.retryTitle"),
        description: t("twoFactor.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (code: string) =>
      apiRequest("POST", "/api/auth/2fa/confirm", {
        secret: setupData!.secret,
        code,
        backupCodes: setupData!.backupCodes,
      }),
    onSuccess: () => {
      toast({
        title: t("twoFactor.toast.enabledTitle"),
        description: t("twoFactor.toast.enabledDesc"),
      });
      setStep("idle");
      setSetupData(null);
      setVerifyCode("");
      queryClient.invalidateQueries({ queryKey: ["/api/auth/2fa/status"] });
    },
    onError: (error: Error) => {
      toast({
        title: t("twoFactor.toast.invalidCodeTitle"),
        description: t("twoFactor.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  const disableMutation = useMutation({
    mutationFn: (code: string) => apiRequest("POST", "/api/auth/2fa/disable", { code }),
    onSuccess: () => {
      toast({
        title: t("twoFactor.toast.disabledTitle"),
        description: t("twoFactor.toast.disabledDesc"),
      });
      setDisableCode("");
      queryClient.invalidateQueries({ queryKey: ["/api/auth/2fa/status"] });
    },
    onError: (error: Error) => {
      toast({
        title: t("twoFactor.toast.retryTitle"),
        description: t("twoFactor.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  const copyBackupCodes = () => {
    if (!setupData) return;
    navigator.clipboard.writeText(setupData.backupCodes.join("\n"));
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  if (isEnabled) {
    return (
      <Card className="bg-card border-white/[0.08]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-green-400" />
            {t("twoFactor.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-green-400 text-sm">{t("twoFactor.activeNotice")}</p>
          <div className="flex items-center gap-2">
            <Input
              type="text"
              placeholder={t("twoFactor.codePlaceholder")}
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              maxLength={8}
              className="bg-white/[0.06] border-white/[0.1] text-white max-w-[250px]"
            />
            <Button
              variant="destructive"
              size="sm"
              onClick={() => disableMutation.mutate(disableCode)}
              disabled={disableMutation.isPending || disableCode.length < 6}
            >
              <ShieldOff className="w-4 h-4 mr-1" />
              {t("twoFactor.disable")}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === "scan" && setupData) {
    return (
      <Card className="bg-card border-white/[0.08]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-yellow-400" />
            {t("twoFactor.setupStep1")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="text-white/70 text-sm mb-3">{t("twoFactor.scanInstruction")}</p>
            <div className="flex justify-center">
              <img
                src={setupData.qrCodeDataUrl}
                alt="QR Code 2FA"
                className="w-48 h-48 rounded-lg bg-white p-2"
              />
            </div>
          </div>

          <div>
            <p className="text-white/70 text-sm mb-2">{t("twoFactor.backupCodesTitle")}</p>
            <div className="bg-black/40 rounded-lg p-3 font-mono text-sm text-white/80 grid grid-cols-2 gap-1">
              {setupData.backupCodes.map((code) => (
                <span key={code}>{code}</span>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 text-white/70 border-white/[0.1]"
              onClick={copyBackupCodes}
            >
              {copiedCodes ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copiedCodes ? t("twoFactor.copied") : t("twoFactor.copyCodes")}
            </Button>
          </div>

          <div>
            <p className="text-white/70 text-sm mb-2">{t("twoFactor.enterCode")}</p>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                className="bg-white/[0.06] border-white/[0.1] text-white max-w-[180px] text-center text-lg tracking-widest"
              />
              <Button
                onClick={() => confirmMutation.mutate(verifyCode)}
                disabled={confirmMutation.isPending || verifyCode.length !== 6}
                className="bg-green-600 hover:bg-green-700"
              >
                {t("twoFactor.confirm")}
              </Button>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="text-white/50"
            onClick={() => {
              setStep("idle");
              setSetupData(null);
            }}
          >
            {t("twoFactor.cancel")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-white/[0.08]">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Shield className="w-5 h-5 text-yellow-400" />
          {t("twoFactor.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-white/60 text-sm">{t("twoFactor.intro")}</p>
        <Button
          onClick={() => setupMutation.mutate()}
          disabled={setupMutation.isPending}
          className="bg-yellow-600 hover:bg-yellow-700"
        >
          <Shield className="w-4 h-4 mr-2" />
          {setupMutation.isPending ? t("twoFactor.initializing") : t("twoFactor.enable")}
        </Button>
      </CardContent>
    </Card>
  );
}
